import React, { useMemo, useEffect, useRef } from 'react';
import * as THREE from 'three';
import { PalletConfigObj } from 'blackbox';
import {
	getOptimizedPalletMesh,
	getBoxDimensions,
	getRotationAngle,
	getDenormalizedPosition,
	getSortedLayout,
} from './utils';
import { getSlipSheet, LayoutTransform } from 'blackbox';

import { useInstancedBoxes } from './hooks/useInstancedBoxes';
import { useInstancedSlipsheets } from './hooks/useInstancedSlipsheets';
import { useHighlightMesh } from './hooks/useHighlightMesh';
import { useAssets } from '../../context/AssetsContext';
import { Box3, Vector3 } from 'three';

type ItemType = 'box' | 'slipsheet';
const Z_OFFSET = 4;

interface PalletGroupProps {
	/** A THREE.Scene or any THREE.Object3D to which we'll attach our pallet stuff */
	parentScene: THREE.Scene | THREE.Group | null;

	palletConfig: PalletConfigObj;
	stackIndex: number;
	palletIndex: number;
	bayIndex: number;

	/** The current "position" (how many items are shown), or undefined => all items */
	position?: number;
	/** Whether or not to show the green highlight */
	useHighlights?: boolean;
	applyOffset?: (length: number, width: number) => void;
	scaleScene?: (scale?: number) => void;
}

/**
 * A "headless" React component that attaches a pallet (boxes, slipsheets, highlight)
 * to a given parentScene. It does NOT create its own <canvas> or Three.js scene/camera.
 */
export function PalletGroup({
	parentScene,
	palletConfig,
	stackIndex,
	palletIndex,
	bayIndex,
	position,
	useHighlights = true,
	applyOffset,
	scaleScene,
}: PalletGroupProps) {
	const assets = useAssets();
	// -----------------------------------------
	// 1) Compute our actor positions
	// -----------------------------------------

	const computedData = useMemo(() => {
		const hydrated = PalletConfigObj.from(palletConfig);
		const pallet = palletConfig.config.pallets[palletIndex];
		const stack = pallet.Stack[stackIndex];
		const totalBoxes: { matrix: THREE.Matrix4 }[] = [];
		const totalSlipsheets: { matrix: THREE.Matrix4 }[] = [];
		const positions: ItemType[] = [];
		let labelPos = 0;
		let computedTotalHeight = pallet.Dimensions.height;

		// Preallocated reusable objects
		const tmpMatrix = new THREE.Matrix4();
		const tmpPos = new THREE.Vector3();
		const tmpQuat = new THREE.Quaternion();
		const tmpScale = new THREE.Vector3();
		const tmpEuler = new THREE.Euler(); // for setting from angles
		
		stack.layers.forEach((layer, layerIndex) => {
			let layout = LayoutTransform.Transform(
				layer.transform,
				pallet,
				palletConfig.config.boxes,
				layer.index
			);
			if (bayIndex >= 0) {
				layout = getSortedLayout(
					hydrated,
					palletIndex,
					stackIndex,
					layerIndex,
					bayIndex
				);
			}

			const box = palletConfig.config.boxes.find(b => b.id === layout?.box_id);
			if (!box) return;

			if (!labelPos) {
				labelPos = box.labelSide;
			}

			// Process slipsheet if available BEFORE boxes
			const slipsheet = getSlipSheet(
				layerIndex,
				stack.layers.length,
				palletConfig.config.slipsheets,
				stack.slipSheet
			);
			if (slipsheet) {
				positions.push('slipsheet');
				const width = Math.max(slipsheet.dimensions.height, 1);
				tmpPos.set(0, 0, computedTotalHeight + width / 2 + Z_OFFSET);
				tmpQuat.identity();
				tmpScale.set(pallet.Dimensions.width, pallet.Dimensions.length, width);
				tmpMatrix.compose(tmpPos, tmpQuat, tmpScale);
				totalSlipsheets.push({ matrix: tmpMatrix.clone() });
				computedTotalHeight += width;
			}

			// Now process the boxes on top of the slipsheet
			layout?.boxPositions.forEach(boxPosition => {
				const finalDims = getBoxDimensions(
					box,
					boxPosition.multipick,
					boxPosition.multipickAlongWidth
				);
				const finalRot = getRotationAngle(boxPosition.rotated, boxPosition.spin);
				const finalPos = getDenormalizedPosition(
					boxPosition,
					finalDims,
					pallet.Dimensions,
					boxPosition.rotated
				);

				tmpPos.set(
					finalPos.x,
					finalPos.y,
					computedTotalHeight + finalDims.height / 2 + Z_OFFSET
				);
				tmpEuler.set(0, 0, finalRot);
				tmpQuat.setFromEuler(tmpEuler);
				tmpScale.set(finalDims.width, finalDims.length, finalDims.height);
				tmpMatrix.compose(tmpPos, tmpQuat, tmpScale);
				totalBoxes.push({ matrix: tmpMatrix.clone() });
				positions.push('box');
			});

			computedTotalHeight += box.dimensions.height * 1.003;
		});

		// Final topmost slipsheet (if it exists)
		const topSlipsheet = getSlipSheet(
			stack.layers.length,
			stack.layers.length,
			palletConfig.config.slipsheets,
			stack.slipSheet
		);
		if (topSlipsheet) {
			positions.push('slipsheet');
			const width = Math.max(topSlipsheet.dimensions.height, 1);
			tmpPos.set(0, 0, computedTotalHeight + width / 2 + Z_OFFSET);
			tmpQuat.identity();
			tmpScale.set(pallet.Dimensions.width, pallet.Dimensions.length, width);
			tmpMatrix.compose(tmpPos, tmpQuat, tmpScale);
			totalSlipsheets.push({ matrix: tmpMatrix.clone() });
			computedTotalHeight += width;
		}

		return {
			pallet,
			totalBoxes,
			totalSlipsheets,
			labelPosition: labelPos,
			positions,
		};
	}, [palletConfig, palletIndex, stackIndex, bayIndex]);

	// -----------------------------------------
	// 2) "Install" the pallet mesh into parentScene
	// -----------------------------------------
	// We keep a ref for the pallet group so we can remove it on unmount
	const palletGroupRef = useRef<THREE.Group | null>(null);
	const palletOutlineGroupRef = useRef<THREE.Group | null>(null);

	useEffect(() => {
		if (!parentScene) return;
		const { width, length, height } = computedData.pallet.Dimensions;
		const palletGroup = getOptimizedPalletMesh(
			width,
			length,
			height,
			assets.woodMaterial!
		);

		// Position the pallet group so it sits halfway above the ground
		palletGroup.position.set(0, 0, height / 2);

		// Add both groups to the scene
		parentScene.add(palletGroup);

		// Store references for cleanup
		palletGroupRef.current = palletGroup;
		if(applyOffset) applyOffset(length, width);
		if(scaleScene) scaleScene();

		return () => {
			// Cleanup: remove from parent and dispose geometries and materials
			if (palletGroupRef.current) {
				parentScene.remove(palletGroupRef.current);
				// Dispose of all child meshes
				palletGroupRef.current.traverse(child => {
					if (child instanceof THREE.Mesh) {
						child.geometry.dispose();
						if (Array.isArray(child.material)) {
							child.material.forEach(m => m.dispose());
						} else {
							child.material.dispose();
						}
					}
				});
			}

			if (palletOutlineGroupRef.current) {
				parentScene.remove(palletOutlineGroupRef.current);
				// Dispose of all child meshes
				palletOutlineGroupRef.current.traverse(child => {
					if (child instanceof THREE.Mesh) {
						child.geometry.dispose();
						if (Array.isArray(child.material)) {
							child.material.forEach(m => m.dispose());
						} else {
							child.material.dispose();
						}
					}
				});
			}
		};
	}, [parentScene, computedData.pallet]);

	// -----------------------------------------
	// 3) Instanced Boxes
	// -----------------------------------------
	const boxMatrices = useMemo(
		() => computedData.totalBoxes.map(b => b.matrix),
		[computedData.totalBoxes]
	);
	const { instancedBoxesRef } = useInstancedBoxes({
		scene: parentScene,
		count: boxMatrices.length,
		labelPosition: computedData.labelPosition,
		boxMatrices,
	});

	// -----------------------------------------
	// 4) Instanced Slipsheets
	// -----------------------------------------
	const slipSheetMatrices = useMemo(
		() => computedData.totalSlipsheets.map(s => s.matrix),
		[computedData.totalSlipsheets]
	);
	const { instancedSlipsheetsRef } = useInstancedSlipsheets({
		scene: parentScene,
		slipSheetMatrices,
		cardboardMaterial: assets.cardboardMaterial!,
	});

	// -----------------------------------------
	// 5) Highlight Mesh
	// -----------------------------------------
	const { highlightMeshRef, setHighlightMatrix } = useHighlightMesh(parentScene);

	// -----------------------------------------
	// 6) Position effect (mask items after "position")
	// -----------------------------------------

	useEffect(() => {
		if (
			!parentScene ||
			!instancedBoxesRef.current ||
			!instancedSlipsheetsRef.current
		) {
			return;
		}

		const { positions } = computedData;
		if (!positions.length) return;

		const effectivePosition = Math.min(
			position !== undefined ? position : positions.length,
			positions.length
		);
		const highlightIndex = effectivePosition - 1;

		// If index is negative, hide everything.
		if (highlightIndex < 0) {
			instancedBoxesRef.current.count = 0;
			instancedSlipsheetsRef.current.count = 0;
			if (highlightMeshRef.current) {
				highlightMeshRef.current.visible = false;
			}
			return;
		}

		const lastItemType = positions[highlightIndex];
		// Count how many boxes/slipsheets come before highlightIndex
		let boxIndex = 0;
		let slipIndex = 0;
		for (let i = 0; i < highlightIndex; i++) {
			if (positions[i] === 'box') boxIndex++;
			else slipIndex++;
		}

		// Update instanced boxes
		instancedBoxesRef.current.count =
			lastItemType === 'box' ? boxIndex + 1 : boxIndex;
		instancedBoxesRef.current.instanceMatrix.needsUpdate = true;

		// Update slipsheets
		instancedSlipsheetsRef.current.count =
			lastItemType === 'slipsheet' ? slipIndex + 1 : slipIndex;
		instancedSlipsheetsRef.current.instanceMatrix.needsUpdate = true;

		// Highlight
		if (!useHighlights || !highlightMeshRef.current) {
			if (highlightMeshRef.current) {
				highlightMeshRef.current.visible = false;
			}
			return;
		}

		highlightMeshRef.current.visible = true;
		if (lastItemType === 'box') {
			const boxMatrix = boxMatrices[boxIndex];
			setHighlightMatrix(boxMatrix);
		} else {
			const slipMatrix = slipSheetMatrices[slipIndex];
			setHighlightMatrix(slipMatrix);
		}
	}, [position, useHighlights, computedData, parentScene]);

	// -----------------------------------------
	// This component renders nothing visually in DOM;
	// it just attaches 3D objects to your "parentScene"
	// -----------------------------------------
	return null;
}
